import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddSpecialPermissionsApplicationErrors,
  AddSpecialPermissionsResponse,
  DataPermissions,
  GetMyPermissionsApplicationErrors,
  GetMyPermissionsResponse,
  GetPermissionsApplicationErrors,
  GetPermissionsResponse,
  RemoveSpecialPermissionsApplicationErrors,
  SpecialPermissions,
  UpdatePermissionsApplicationErrors,
  UpdatePermissionsResponse,
  UpdateSpecialPermissions,
  UpdateSpecialPermissionsApplicationErrors,
  UpdateSpecialPermissionsResponse,
  addSpecialPermissions as universalAddSpecialPermissions,
  getMyPermissions as universalGetMyPermissions,
  getPermissions as universalGetPermissions,
  removeSpecialPermissions as universalRemoveSpecialPermissions,
  updatePermissions as universalUpdatePermissions,
  updateSpecialPermissions as universalUpdateSpecialPermissions,
} from './data-v1-data-permissions-permissions.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/data' };

export function getPermissions(
  httpClient: HttpClient
): GetPermissionsSignature {
  return (dataCollectionId: string) =>
    universalGetPermissions(
      dataCollectionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPermissionsSignature {
  /**
   * Retrieves the current permissions for the specified data collection.
   * @param - ID of the collection to retrieve permissions of.
   */
  (dataCollectionId: string): Promise<
    NonNullablePaths<
      GetPermissionsResponse,
      | `dataPermissions._id`
      | `dataPermissions.itemRead`
      | `dataPermissions.itemInsert`
      | `dataPermissions.itemUpdate`
      | `dataPermissions.itemRemove`
      | `dataPermissions.specialPermissions`
      | `dataPermissions.specialPermissions.${number}.userId`
      | `dataPermissions.specialPermissions.${number}.policyId`
      | `dataPermissions.specialPermissions.${number}._id`
      | `dataPermissions.specialPermissions.${number}.itemRead`
      | `dataPermissions.specialPermissions.${number}.itemInsert`
      | `dataPermissions.specialPermissions.${number}.itemUpdate`
      | `dataPermissions.specialPermissions.${number}.itemRemove`,
      5
    > & {
      __applicationErrorsType?: GetPermissionsApplicationErrors;
    }
  >;
}

export function updatePermissions(
  httpClient: HttpClient
): UpdatePermissionsSignature {
  return (
    dataPermissions: NonNullablePaths<
      DataPermissions,
      `_id` | `itemInsert` | `itemRead` | `itemRemove` | `itemUpdate`,
      2
    >
  ) =>
    universalUpdatePermissions(
      dataPermissions,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdatePermissionsSignature {
  /**
   * Updates permissions for the specified data collection.
   *
   * > **Note:** To update special permissions, call [Update Special Permissions](https://dev.wix.com/docs/rest/business-solutions/cms/data-permissions/update-special-permissions).
   * @param - Permissions to update.
   */
  (
    dataPermissions: NonNullablePaths<
      DataPermissions,
      `_id` | `itemInsert` | `itemRead` | `itemRemove` | `itemUpdate`,
      2
    >
  ): Promise<
    NonNullablePaths<
      UpdatePermissionsResponse,
      | `dataPermissions._id`
      | `dataPermissions.itemRead`
      | `dataPermissions.itemInsert`
      | `dataPermissions.itemUpdate`
      | `dataPermissions.itemRemove`
      | `dataPermissions.specialPermissions`
      | `dataPermissions.specialPermissions.${number}.userId`
      | `dataPermissions.specialPermissions.${number}.policyId`
      | `dataPermissions.specialPermissions.${number}._id`
      | `dataPermissions.specialPermissions.${number}.itemRead`
      | `dataPermissions.specialPermissions.${number}.itemInsert`
      | `dataPermissions.specialPermissions.${number}.itemUpdate`
      | `dataPermissions.specialPermissions.${number}.itemRemove`,
      5
    > & {
      __applicationErrorsType?: UpdatePermissionsApplicationErrors;
    }
  >;
}

export function addSpecialPermissions(
  httpClient: HttpClient
): AddSpecialPermissionsSignature {
  return (dataCollectionId: string, specialPermissions: SpecialPermissions) =>
    universalAddSpecialPermissions(
      dataCollectionId,
      specialPermissions,
      // @ts-ignore
      { httpClient }
    );
}

interface AddSpecialPermissionsSignature {
  /**
   * Adds special permissions to a specific user or role for the specified data collection.
   *
   * Special permissions allow specific users or roles to perform actions that [collection-level permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/data-permissions/introduction#collection-level-permissions) restrict.
   * @param - ID of the collection for which to grant special permissions.
   * @param - Special permissions to grant to the user or role for the specified collection.
   */
  (dataCollectionId: string, specialPermissions: SpecialPermissions): Promise<
    NonNullablePaths<
      AddSpecialPermissionsResponse,
      | `specialPermissions.userId`
      | `specialPermissions.policyId`
      | `specialPermissions._id`
      | `specialPermissions.itemRead`
      | `specialPermissions.itemInsert`
      | `specialPermissions.itemUpdate`
      | `specialPermissions.itemRemove`,
      3
    > & {
      __applicationErrorsType?: AddSpecialPermissionsApplicationErrors;
    }
  >;
}

export function updateSpecialPermissions(
  httpClient: HttpClient
): UpdateSpecialPermissionsSignature {
  return (_id: string, specialPermissions: UpdateSpecialPermissions) =>
    universalUpdateSpecialPermissions(
      _id,
      specialPermissions,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateSpecialPermissionsSignature {
  /**
   * Updates the special permissions for the specified user or role.
   *
   * > **Note:** Special permissions that aren't explicitly included in the request are set to `UNSPECIFIED` and replaced by [collection-level permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/data-permissions/introduction#collection-level-permissions).
   * @param - ID of the special permissions set.
   */
  (_id: string, specialPermissions: UpdateSpecialPermissions): Promise<
    NonNullablePaths<
      UpdateSpecialPermissionsResponse,
      | `specialPermissions.userId`
      | `specialPermissions.policyId`
      | `specialPermissions._id`
      | `specialPermissions.itemRead`
      | `specialPermissions.itemInsert`
      | `specialPermissions.itemUpdate`
      | `specialPermissions.itemRemove`,
      3
    > & {
      __applicationErrorsType?: UpdateSpecialPermissionsApplicationErrors;
    }
  >;
}

export function removeSpecialPermissions(
  httpClient: HttpClient
): RemoveSpecialPermissionsSignature {
  return (specialPermissionsId: string) =>
    universalRemoveSpecialPermissions(
      specialPermissionsId,
      // @ts-ignore
      { httpClient }
    );
}

interface RemoveSpecialPermissionsSignature {
  /**
   * Removes special permissions from a specific user or role.
   *
   * When you remove special permissions, [collection-level permissions](https://dev.wix.com/docs/api-reference/business-solutions/cms/data-permissions/introduction#collection-level-permissions) apply to the user or role for all actions.
   * @param - ID of the special permissions to remove.
   */
  (specialPermissionsId: string): Promise<
    void & {
      __applicationErrorsType?: RemoveSpecialPermissionsApplicationErrors;
    }
  >;
}

export function getMyPermissions(
  httpClient: HttpClient
): GetMyPermissionsSignature {
  return (dataCollectionId: string) =>
    universalGetMyPermissions(
      dataCollectionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetMyPermissionsSignature {
  /**
   * Retrieves the current user's permissions for the specified data collection.
   * @param - ID of the collection for which to check the current user's permissions.
   */
  (dataCollectionId: string): Promise<
    NonNullablePaths<
      GetMyPermissionsResponse,
      `itemRead` | `itemInsert` | `itemUpdate` | `itemRemove`,
      2
    > & {
      __applicationErrorsType?: GetMyPermissionsApplicationErrors;
    }
  >;
}

export {
  Access,
  AccessLevel,
  ActionEvent,
  AddSpecialPermissionsRequest,
  AddSpecialPermissionsResponse,
  Condition,
  ConditionOperator,
  ConditionOperatorOperatorsOneOf,
  ConditionType,
  ConditionTypeOfOneOf,
  ConditionValue,
  ConditionValueValueOneOf,
  DataPermissions,
  DependOnOperator,
  DomainEvent,
  DomainEventBodyOneOf,
  DraftItemsPluginPermissions,
  Effect,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EnvironmentCondition,
  EnvironmentConditionConditionOneOf,
  EqualOperator,
  ExperimentCondition,
  ExperimentOperator,
  GetMyPermissionsRequest,
  GetMyPermissionsResponse,
  GetMyPermissionsResponseDraftItemsPluginPermissions,
  GetPermissionsRequest,
  GetPermissionsResponse,
  JoinedCondition,
  JoinedConditionOperator,
  LikeOperator,
  MigrateCustomRolesRequest,
  MigrateCustomRolesResponse,
  PolicyAssigned,
  PolicyAssignment,
  PolicyAssignmentUpdated,
  PolicyAssignmentsUpdated,
  PolicyAssignmentsUpdatedEventOneOf,
  PolicyAuthorizationDataUpdated,
  PolicyCondition,
  PolicyOwner,
  PolicyOwnerType,
  PolicyStatement,
  PolicyType,
  PolicyUnassigned,
  RemoveSpecialPermissionsRequest,
  RemoveSpecialPermissionsResponse,
  Resource,
  ResourcePath,
  RestoreInfo,
  SimpleCondition,
  SimpleConditionOperator,
  SimpleConditionValue,
  SimpleConditionValueValueOneOf,
  SpecialPermissions,
  SpecialPermissionsDraftItemsPluginPermissions,
  SpecialPermissionsSubjectOneOf,
  Subject,
  SubjectContext,
  SubjectContextType,
  SubjectType,
  UpdatePermissionsRequest,
  UpdatePermissionsResponse,
  UpdateSpecialPermissions,
  UpdateSpecialPermissionsRequest,
  UpdateSpecialPermissionsResponse,
} from './data-v1-data-permissions-permissions.universal.js';
